import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { SimilarPositionType, useJobPositionsService } from '@restworld/data-services';
import { JobSimilarPositionsType, useResponsiveBreakPoints } from '@restworld/utils-common';

// Page size matches the grid breakpoint in `SimilarJobsList`:
// - Mobile (<768px) renders one card per row → 3 fits without scrolling.
// - Tablet+ (≥768px) renders 2x2 → 4 fills the grid.
const MOBILE_PAGE_SIZE = 3;
const DESKTOP_PAGE_SIZE = 4;

// Paginated similar-JPs fetcher used by both the offline-JP modal and the
// "Posizioni simili" section at the bottom of the JP page. Page 1 goes
// through TanStack Query so the two surfaces share a single network
// request when both mount on the same page. Subsequent pages ("Mostra
// altre") are appended via the service directly — they're per-surface and
// don't need to be cached.
export const useSimilarJobPositionsPaginated = (jobPositionId?: string) => {
  const service = useJobPositionsService();
  const { isMobile } = useResponsiveBreakPoints();
  const pageSize = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;
  const [extraPages, setExtraPages] = useState<JobSimilarPositionsType[][]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreOverride, setHasMoreOverride] = useState<boolean | null>(null);

  // pageSize is part of the cache key — resizing across the breakpoint
  // re-fetches with the new limit (rare; user typically lands on one
  // device and stays there).
  const firstPage = useQuery({
    queryKey: ['similar-job-positions', jobPositionId, 0, pageSize],
    queryFn: async () => {
      const res = await service.similarJobPositions(jobPositionId as string, { limit: pageSize, offset: 0 });
      return (res?.data ?? []).map(formatSimilar);
    },
    enabled: !!jobPositionId,
  });

  const items = useMemo(() => [...(firstPage.data ?? []), ...extraPages.flat()], [firstPage.data, extraPages]);
  const hasMore = hasMoreOverride ?? (firstPage.data?.length ?? 0) === pageSize;

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !jobPositionId) return;
    setIsLoadingMore(true);
    try {
      const res = await service.similarJobPositions(jobPositionId, { limit: pageSize, offset: items.length });
      const formatted = (res?.data ?? []).map(formatSimilar);
      setExtraPages((prev) => [...prev, formatted]);
      if (formatted.length < pageSize) setHasMoreOverride(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, items.length, jobPositionId, pageSize, service]);

  return {
    items,
    hasMore,
    isLoading: isLoadingMore || firstPage.isLoading,
    isInitialLoading: firstPage.isLoading,
    error: firstPage.error,
    loadMore,
  };
};

const formatSimilar = (j: SimilarPositionType): JobSimilarPositionsType => ({
  id: j.id,
  city: j.restaurant?.locality ?? '',
  restaurantName: j.restaurant?.restaurant_name ?? '',
  restaurantLogo: j.restaurant?.restaurant_logo,
  publishedAt: j.published_at,
  jobTitle: j.job_title,
  jobType: j.job_type_var?.label,
  occupation: j.occupation?.label_it?.label,
  salaryMin: j.min_salary,
  salaryMax: j.max_salary,
  salary: j.salary,
  showRestaurantDetails: j.show_restaurant_details,
  redirectUrl: j.semantic_unique_name ? `/posizione/${j.semantic_unique_name}` : '',
});
